/**
 * Web Bluetooth LE Manager for PalmSens Device Communication
 * Handles Bluetooth LE device connection, disconnection, and data transmission using Web Bluetooth API
 *
 * Note: Web Bluetooth API works on Chrome, Edge, and Opera (not Firefox or Safari)
 * Based on PalmSens BLE implementation using GATT services
 */

import { ConnectionState } from 'methodscript-device';
import type { BleDeviceInfo } from 'methodscript-device';
import { logger } from 'shared-monaco-methodscript';

/**
 * GATT Service and Characteristic UUIDs for PalmSens devices
 */
const GATT_SERVICE_UUIDS = {
    // Primary service UUID
    PRIMARY_SERVICE: '569a1101-b87f-490c-92cb-11ba5ea5167c',
    // Alternative service UUID (for BT900)
    ALTERNATIVE_SERVICE: '2456e1b9-26e2-8f83-e744-f34f01e9d701',
};

const GATT_CHARACTERISTIC_UUIDS = {
    // Characteristics for primary service
    PRIMARY_READ: '569a2000-b87f-490c-92cb-11ba5ea5167c',
    PRIMARY_WRITE: '569a2001-b87f-490c-92cb-11ba5ea5167c',
    // Characteristics for alternative service (read and write use same UUID)
    ALTERNATIVE_RW: '2456e1b9-26e2-8f83-e744-f34f01e9d703',
};

type ServiceType = 'primary' | 'alternative';

/**
 * BLE communication constants
 */
/** Maximum Transmission Unit (MTU) size in bytes for BLE packets */
const BLE_MTU_SIZE = 20;
/** Delay (ms) between BLE chunks to prevent overwhelming the device */
const BLE_CHUNK_DELAY_MS = 10;

export class WebBLEManager extends EventTarget {
    private device: BluetoothDevice | null;
    private server: BluetoothRemoteGATTServer | null;
    private service: BluetoothRemoteGATTService | null;
    private readCharacteristic: BluetoothRemoteGATTCharacteristic | null;
    private writeCharacteristic: BluetoothRemoteGATTCharacteristic | null;
    private state: ConnectionState;
    private readBuffer: string[];
    private isSupported: boolean;
    private boundOnCharacteristicValueChanged = this.onCharacteristicValueChanged.bind(this);
    private boundOnDeviceDisconnected = this.onDeviceDisconnected.bind(this);

    constructor() {
        super();
        this.device = null;
        this.server = null;
        this.service = null;
        this.readCharacteristic = null;
        this.writeCharacteristic = null;
        this.state = ConnectionState.Disconnected;
        this.readBuffer = [];

        // Check Web Bluetooth API support
        this.isSupported = 'bluetooth' in navigator;
    }

    /**
     * Check if Web Bluetooth API is supported in the current browser
     */
    isWebBluetoothSupported(): boolean {
        return this.isSupported;
    }

    /**
     * Get current connection state
     */
    getState(): ConnectionState {
        return this.state;
    }

    /**
     * Get current device info
     */
    getDeviceInfo(): BleDeviceInfo | null {
        if (!this.device) {
            return null;
        }

        return {
            name: this.device.name,
            id: this.device.id,
            connected: this.device.gatt?.connected || false,
        };
    }

    /**
     * Set connection state and dispatch event
     */
    private setState(newState: ConnectionState): void {
        if (this.state === newState) {
            return;
        }

        this.state = newState;
        this.dispatchEvent(new CustomEvent('stateChange', { detail: { state: newState } }));
    }

    /**
     * Request device selection and connect
     */
    async connect(): Promise<void> {
        logger.debug('[WebBLE] Connect called, current state:', this.state);

        if (!this.isSupported) {
            throw new Error('Web Bluetooth API is not supported in this browser. Please use Chrome, Edge, or Opera.');
        }

        if (this.state === ConnectionState.Connected) {
            logger.warn('[WebBLE] Already connected, disconnecting first...');
            await this.disconnect();
        }

        if (this.state === ConnectionState.Connecting) {
            throw new Error('Connection already in progress');
        }

        this.setState(ConnectionState.Connecting);

        try {
            // Request Bluetooth device from user
            // Filter for devices that start with "PS" or are named "PalmSens" or "ANNA"
            logger.debug('[WebBLE] Requesting device selection...');
            this.device = await navigator.bluetooth.requestDevice({
                filters: [{ namePrefix: 'PS' }, { name: 'PalmSens' }, { namePrefix: 'ANNA' }, { namePrefix: 'Anna' }, { namePrefix: 'anna' }],
                optionalServices: [GATT_SERVICE_UUIDS.PRIMARY_SERVICE, GATT_SERVICE_UUIDS.ALTERNATIVE_SERVICE],
            });

            logger.debug('[WebBLE] Device selected:', this.device.name, 'ID:', this.device.id);

            // Set up disconnect handler
            this.device.addEventListener('gattserverdisconnected', this.boundOnDeviceDisconnected);

            // Connect to GATT server
            logger.debug('[WebBLE] Connecting to GATT server...');
            this.server = await this.device.gatt!.connect();
            logger.debug('[WebBLE] Connected to GATT server');

            // Try to connect to primary service first, then alternative
            let connected = false;
            try {
                logger.debug('[WebBLE] Attempting to connect to primary service...');
                await this.connectToService(GATT_SERVICE_UUIDS.PRIMARY_SERVICE, 'primary');
                connected = true;
                logger.debug('[WebBLE] Connected to primary service');
            } catch (primaryError) {
                logger.debug('[WebBLE] Primary service not available, trying alternative...', primaryError);
                try {
                    await this.connectToService(GATT_SERVICE_UUIDS.ALTERNATIVE_SERVICE, 'alternative');
                    connected = true;
                    logger.debug('[WebBLE] Connected to alternative service');
                } catch (alternativeError) {
                    logger.error('[WebBLE] No compatible GATT service found:', alternativeError);
                    throw new Error('Device does not have a compatible GATT service');
                }
            }

            if (!connected) {
                throw new Error('Failed to connect to GATT service');
            }

            this.setState(ConnectionState.Connected);
            logger.debug('[WebBLE] Connection established successfully');
            this.dispatchEvent(new CustomEvent('connected', { detail: this.getDeviceInfo() }));
        } catch (error) {
            logger.error('[WebBLE] Connection failed:', error);
            if (error instanceof Error) {
                logger.error('[WebBLE] Error details:', {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                });
            }
            this.setState(ConnectionState.Error);
            await this.cleanup();
            throw error;
        }
    }

    /**
     * Connect to a specific GATT service and set up characteristics
     */
    private async connectToService(serviceUuid: string, serviceType: ServiceType): Promise<void> {
        // Get the GATT service
        this.service = await this.server!.getPrimaryService(serviceUuid);
        logger.debug('[WebBLE] Got service:', serviceUuid);

        // Get characteristics based on service type
        if (serviceType === 'primary') {
            this.readCharacteristic = await this.service.getCharacteristic(GATT_CHARACTERISTIC_UUIDS.PRIMARY_READ);
            this.writeCharacteristic = await this.service.getCharacteristic(GATT_CHARACTERISTIC_UUIDS.PRIMARY_WRITE);
        } else {
            // Alternative service uses same UUID for read and write
            const rwCharacteristic = await this.service.getCharacteristic(GATT_CHARACTERISTIC_UUIDS.ALTERNATIVE_RW);
            this.readCharacteristic = rwCharacteristic;
            this.writeCharacteristic = rwCharacteristic;
        }

        logger.debug('[WebBLE] Got characteristics');

        // Subscribe to notifications
        await this.readCharacteristic.startNotifications();
        this.readCharacteristic.addEventListener('characteristicvaluechanged', this.boundOnCharacteristicValueChanged);
        logger.debug('[WebBLE] Subscribed to notifications');
    }

    /**
     * Handle device disconnection
     */
    private onDeviceDisconnected(event: Event): void {
        const target = event.target as BluetoothDevice;
        logger.debug('[WebBLE] Device disconnected:', target?.name);
        this.cleanup();
        this.setState(ConnectionState.Disconnected);
        this.dispatchEvent(new CustomEvent('disconnected'));
    }

    /**
     * Handle characteristic value changed (data received)
     */
    private onCharacteristicValueChanged(event: Event): void {
        const target = event.target as BluetoothRemoteGATTCharacteristic;
        const value = target.value;
        const decoder = new TextDecoder('utf-8');
        const text = decoder.decode(value);

        logger.debug('[WebBLE] Received:', text.length, 'characters');

        // Add to read buffer
        this.readBuffer.push(text);

        // Dispatch received event
        this.dispatchEvent(
            new CustomEvent('dataReceived', {
                detail: {
                    data: text,
                    bytes: value?.byteLength || 0,
                },
            }),
        );
    }

    /**
     * Disconnect from device
     */
    async disconnect(): Promise<void> {
        if (this.state === ConnectionState.Disconnected) {
            return;
        }

        logger.debug('[WebBLE] Disconnecting...');
        await this.cleanup();
        this.setState(ConnectionState.Disconnected);
        this.dispatchEvent(new CustomEvent('disconnected'));
    }

    /**
     * Clean up device resources
     */
    private async cleanup(): Promise<void> {
        logger.debug('[WebBLE] Cleanup called');

        // Unsubscribe from notifications
        try {
            if (this.readCharacteristic) {
                await this.readCharacteristic.stopNotifications();
                this.readCharacteristic.removeEventListener('characteristicvaluechanged', this.boundOnCharacteristicValueChanged);
            }
        } catch (error) {
            logger.warn('[WebBLE] Error stopping notifications:', error);
        } finally {
            this.readCharacteristic = null;
        }

        this.writeCharacteristic = null;
        this.service = null;

        // Disconnect from GATT server
        try {
            if (this.server && this.server.connected) {
                logger.debug('[WebBLE] Disconnecting from GATT server...');
                this.server.disconnect();
                logger.debug('[WebBLE] Disconnected from GATT server');
            }
        } catch (error) {
            logger.warn('[WebBLE] Error disconnecting from GATT server:', error);
        } finally {
            this.server = null;
        }

        // Remove event listener and release device
        try {
            if (this.device) {
                this.device.removeEventListener('gattserverdisconnected', this.boundOnDeviceDisconnected);
            }
        } finally {
            this.device = null;
        }

        this.readBuffer = [];
    }

    /**
     * Send MethodScript code to device
     * BLE has a 20-byte packet size limit, so we need to chunk the data
     */
    async sendScript(script: string): Promise<void> {
        logger.debug('[WebBLE] sendScript called');
        logger.debug('[WebBLE] Script length:', script.length, 'chars');
        logger.debug('[WebBLE] Script preview:', script.substring(0, 100));

        if (this.state !== ConnectionState.Connected || !this.device || !this.writeCharacteristic) {
            throw new Error('Not connected to a device');
        }

        try {
            // Convert string to bytes
            const encoder = new TextEncoder();
            const bytes = encoder.encode(script);
            logger.debug('[WebBLE] Total bytes to send:', bytes.length);

            // BLE has a limited MTU, so send in chunks
            const chunkSize = BLE_MTU_SIZE;
            let offset = 0;

            while (offset < bytes.length) {
                const chunkEnd = Math.min(offset + chunkSize, bytes.length);
                const chunk = bytes.slice(offset, chunkEnd);

                logger.debug('[WebBLE] Sending chunk:', offset, '-', chunkEnd, '/', bytes.length);
                await this.writeCharacteristic.writeValue(chunk);

                offset = chunkEnd;

                // Small delay between chunks to prevent overwhelming the device
                if (offset < bytes.length) {
                    await new Promise((resolve) => setTimeout(resolve, BLE_CHUNK_DELAY_MS));
                }
            }

            logger.debug('[WebBLE] Script sent successfully');

            // Dispatch sent event
            this.dispatchEvent(
                new CustomEvent('dataSent', {
                    detail: {
                        data: script,
                        bytes: bytes.length,
                    },
                }),
            );
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            logger.error('[WebBLE] Error sending script:', errorMsg);
            this.dispatchEvent(new CustomEvent('error', { detail: { message: errorMsg } }));
            throw new Error(`Failed to send script: ${errorMsg}`);
        }
    }

    /**
     * Read data from buffer
     * This is used for synchronous read operations
     */
    read(): string {
        if (this.readBuffer.length === 0) {
            return '';
        }

        const data = this.readBuffer.join('');
        this.readBuffer = [];
        return data;
    }

    /**
     * Dispose of resources
     */
    dispose(): void {
        void this.cleanup();
    }
}
